<script>
import Layout from "@layouts/auth";
import { authMethods } from "@state/helpers";
import getSecret from "@utils/get-secret";
import DisableAutocomplete from "vue-disable-autocomplete";
import Vue from "vue";
import TwoFactorOptions from "@components/login/TwoFactorOptions";
import CodeVerification from "@components/login/CodeVerification";
import TempPassword from "@components/login/TempPassword";
Vue.use(DisableAutocomplete);

export default {
  page: {
    title: "Log in",
    meta: [{ name: "description", content: `Log in to ${getSecret("TITLE")}` }], // Used in a html meta tag,
  },
  components: {
    Layout,
    TwoFactorOptions,
    CodeVerification,
    TempPassword,
  },
  data() {
    return {
      username: "",
      password: "",
      authError: null,
      tryingToLogIn: false,
      authState: null,
      hidePassword: true,
      textInA2fPanel: "",
      authStep: 1,
      a2fOptions: [],
    };
  },
  computed: {
    getCompanyInfo: function () {
      return this.$store.state.appAuth.companyInfo || null;
    },
    passwordType() {
      return this.hidePassword ? "password" : "text";
    },
    passwordIcon() {
      return this.hidePassword ? "fa-eye" : "fa-eye-slash";
    },
    getA2fText() {
      if (this.textInA2fPanel === "email") {
        return this.$t("a2f_section.email");
      } else if (this.textInA2fPanel === "totp") {
        return this.$t("a2f_section.totp");
      }
      return "";
    },
  },
  mounted() {
    if (this.$route.params && "expiredSession" in this.$route.params) {
      this.$generate_toast(this, "error", this.$t("expired_session"));
    }
    if ("redirectFrom" in this.$route.query && this.$route.query.redirectFrom !== "/") {
      this.$generate_toast(this, "error", this.$t("login_required"));
    }
  },
  methods: {
    ...authMethods,

    tryToLogIn(event) {
      event.preventDefault();
      this.tryingToLogIn = true;
      this.authError = null; // Reset the authError if it existed.
      this.authState = null;

      return this.logIn({
        username: this.username,
        password: this.password,
      })
        .then((data) => {
          this.tryingToLogIn = false;
          this.authState = true;
          if (
            Object(data.user.user_info).hasOwnProperty("is_first_login") &&
            data.user.user_info.is_first_login
          ) {
            this.$emit("firstLogin");
          } else {
            this.$emit("loggedIn");
          }
          if (
            Object(data.user.user_info).hasOwnProperty("is_first_login") &&
            data.user.user_info.is_first_login
          ) {
            this.authStep = 4;
          } else {
            // Redirect to the originally requested page, or to the home page
            this.redirect();
          }
        })
        .catch((error) => {
          this.tryingToLogIn = false;
          if (error.response && error.response.status === 303) {
            this.a2fOptions = error.response.data.opt_required;
            this.authStep = 2;
          } else {
            this.authError = error.response ? error.response.data : error.message;
            if(typeof this.authError === 'object'){
              var txtErrors = '';
              if (this.authError.errors){
                this.authError.errors.forEach(error => {
                  txtErrors += `${this.$t(error['param'])}: ${this.$t(error['msg'])}, `
                });
                this.authError = txtErrors.substring(0, txtErrors.length-2)
              } else {
                this.authError = this.$t(this.authError.error)
              }
            } 
            this.$generate_noty('error', this.authError, this.$t('alerts.error'),  5000, true)
            this.authState = false;
          }
        });
    },

    removeReadonly(event, refName) {
      this.$nextTick(() => {
        event.preventDefault()
        if (event.target.getAttribute('readonly') == 'readonly') {
          event.target.removeAttribute('readonly')
        }
        if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
          this.$refs[refName].focus(); // Enfocar el campo de entrada específico
        }
      })
    },

    displayVerificationSection(option) {
      this.textInA2fPanel = option;
      this.authStep = 3;
    },

    redirect() {
      // Redirect to the originally requested page, or to the home page
      const redirectPath = this.$route.query.redirectFrom
        ? this.$route.query.redirectFrom === "/logout"
          ? null
          : this.$route.query.redirectFrom
        : null;
      this.$router.push(redirectPath || { name: "home" });
    },
  },
};
</script>

<template>


<Layout>
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-8 col-xl-8">
        <div class="card bg-pattern mx-3">
          <!-- Login -->
          <div v-if="authStep === 1" class="card-body pt-4 pb-4 px-4">
            <div class="text-lg-left text-center m-auto">
              <b-row>
                <b-col xl="7" lg="6" md="12" class="pt-lg-5 pt-md-4 pr-lg-4 px-xl-4 px-4">
                  <a href="/">
                    <div class="login-logo-container"
                      ><b-img
                                    v-if="getCompanyInfo"
                                    :src="
                                      getCompanyInfo['light_logo'].url ||
                                      require('@assets/images/logos/logo-dark.png')
                                    "
                                    alt="login"
                                    fluid
                                    :height="150"
                                /></div>
                  </a>
                  <p class="text-muted mb-4 mt-1">
                    {{ $t('login_section.init_msg') }}
                  </p>
                </b-col>
                <b-col class="px-2 text-left">
                  <b-form @submit.prevent="tryToLogIn">
                    <b-form-group
                      id="input-group-1"
                      :label="$t('email')"
                      label-for="input-1"
                    >
                      <b-form-input
                        id="input-1"
                        v-model="username"
                        ref="username"
                        type="email"
                        required
                        :placeholder="$t('login_section.enter_email')"
                        autocomplete="off"
                        :readonly="false"
                        :state="authState"
                        class="input-login"
                        @focusin="(e) => removeReadonly(e, 'username')"
                      ></b-form-input>
                      {{ authState }}
                    </b-form-group>

                    <b-form-group
                      id="password-group"
                      label-for="register-password"
                      class="mb-2 mb-sm-2 md-6 lg-6"
                    >
                      <slot name="label">
                        <label class="d-block"
                          >{{ $t('password') }}
                          <i
                            class="fad"
                            :class="[passwordIcon]"
                            @click="hidePassword = !hidePassword"
                          ></i
                        ></label>
                      </slot>
                      <b-form-input
                        id="input-2"
                        v-model="password"
                        ref="password"
                        :type="passwordType"
                        required
                        :placeholder="$t('login_section.enter_password')"
                        autocomplete="off"
                        :readonly="false"
                        :state="authState"
                        class="input-login"
                        @focusin="(e) => removeReadonly(e, 'password')"
                      ></b-form-input>
                    </b-form-group>

                    <b-form-group id="button-group" class="mt-4 mb-0">
                      <b-button
                        type="submit"
                        variant="primary"
                        class="btn-block py-2 font-18"
                        :disabled="tryingToLogIn"
                      >
                        <span v-if="!tryingToLogIn">
                          {{ $t('login') }}
                        </span>
                        <b-spinner
                          v-if="tryingToLogIn"
                          small
                          label="Spinning"
                          variant="light"
                        >
                        </b-spinner>
                      </b-button>
                    </b-form-group>
                  </b-form>
                </b-col>
              </b-row>
              <!-- <b-row>
                <b-col>
                  <b-form-invalid-feedback :state="!authError">
                    {{ authError }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row> -->
            </div>
          </div>

          <TwoFactorOptions
            v-if="authStep === 2"
            :a2fOptions="a2fOptions"
            :credentials="{ username, password }"
            @optionSelected="displayVerificationSection"
          ></TwoFactorOptions>

          <CodeVerification
            v-if="authStep === 3"
            :authOption="textInA2fPanel"
            :credentials="{ username, password }"
            @loggedIn="redirect"
            @firstLogin="authStep = 4"
          ></CodeVerification>

          <TempPassword
            v-if="authStep === 4"
            @passwordChanged="redirect"
          ></TempPassword>

          <!-- end card-body -->
        </div>
        <!-- end card -->

        <div v-if="authStep === 1" class="row mt-2">
          <div class="col-12 text-center">
            <p>
              <router-link
                tag="a"
                to="/forget-password"
                class="text-white-50 ml-1"
              >
                {{ $t('login_section.forgot_your_password') }}
              </router-link>
            </p>

            <!-- <p class="text-white-50"
              >{{ $t('login_section.dont_have_an_account') }}
              <router-link tag="a" to="/register" class="text-white ml-1"><b>{{ $t('login_section.sign_up') }}</b></router-link>
            </p> -->
          </div>
        </div>
        <div v-if="authStep === 2 || authStep === 3" class="row mt-3">
          <div
            class="col-12 text-center"
          >
            <p class="text-white-50">
              <router-link tag="a" to="/login" class="text-white ml-1"
                ><b>{{ $t('Back to Login') }}</b></router-link
              >
            </p>
          </div>
          <div class="col-12 text-center" v-if="authStep === 4">
            <a
              href="/logout"
              class="col-12 text-center text-white ml-1 font-weight-bold"
            >
              {{ $t('login_section.change_later') }}
            </a>
          </div>
        </div>
      </div>
      <!-- end col -->
    </div>
    <!-- end row -->
  </Layout>
</template>

<style lang="scss" scoped>
@media screen and (min-width: 767px) {
  // .bg-login-image {
  //   background: url('../../assets/images/bg-image.jpeg'); // bg-image-3.jpg
  //   background-position: center;
  //   background-size: cover;
  // }
}
.bg-pattern {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 20px;
  border:1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
}
.input-login {
  background-color: rgba(255,255,255,0.1) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.1);
  color: white;
  letter-spacing: 0.15mm;
}
</style>